"use client";

import { useCallback, useEffect, useState } from "react";

// Обращения в поддержку (03.09): что написали клиенты из кнопки «Поддержка».
// Оператор читает, отвечает и закрывает; ответ уходит автору уведомлением.

type Req = {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string | null;
  userName: string;
  text: string;
  context: string | null;
  status: "open" | "done";
  note: string | null;
  createdAt: string;
  handledAt: string | null;
};

type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

async function opApi<T = unknown>(key: string, action: string, payload?: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetch(`/api?action=${encodeURIComponent(action)}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-admin-key": key },
      body: JSON.stringify({ action, payload }),
    });
    return (await res.json()) as ApiResult<T>;
  } catch {
    return { ok: false, error: "Нет связи с сервером" };
  }
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/**
 * Ник в Telegram из контекста обращения — и сразу ссылкой на личку.
 *
 * 🔴 Ради этого кнопку «Поддержка» в боте и просили (Никита, 21.09): человек
 * пишет из мессенджера, и ответить ему проще всего там же. Ник кладёт бот в
 * `context` строкой «бот Telegram · @nick» — отдельного поля в таблице нет и
 * заводить его ради одной строки незачем, миграция дороже.
 *
 * Пусто — обращение пришло не из бота (или у человека не задан ник): тогда
 * ссылки нет и отвечать надо через консоль, как раньше.
 */
function nickOf(context: string | null): string | null {
  const m = /@([A-Za-z0-9_]{4,32})/.exec(context ?? "");
  return m ? `@${m[1]}` : null;
}

export default function SupportView({ apiKey }: { apiKey: string }) {
  const [status, setStatus] = useState<"open" | "done" | "all">("open");
  const [rows, setRows] = useState<Req[] | null>(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await opApi<{ requests: Req[] }>(apiKey, "opListSupportRequests", { status });
    if (!r.ok) return setErr(r.error);
    setErr("");
    setRows(r.data.requests);
  }, [apiKey, status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function resolve(id: string, reopen = false) {
    setBusy(id);
    const r = await opApi(apiKey, "opResolveSupportRequest", { id, note: note[id] ?? undefined, reopen });
    setBusy(null);
    if (!r.ok) return setErr(r.error);
    await load();
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Поддержка</h2>
        <div className="flex gap-1 rounded-full bg-[var(--op-surface,#f3f3f2)] p-1">
          {(["open", "done", "all"] as const).map((k) => (
            <button
              key={k}
              className={`rounded-full px-3 py-1 text-sm ${status === k ? "bg-black text-white" : ""}`}
              onClick={() => setStatus(k)}
            >
              {k === "open" ? "Открытые" : k === "done" ? "Закрытые" : "Все"}
            </button>
          ))}
        </div>
      </div>
      <HelpChannel apiKey={apiKey} />
      {err && <div className="text-sm text-red-600">{err}</div>}
      {rows === null ? (
        <div className="text-sm opacity-60">Загружаю…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm opacity-60">Обращений нет.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-black/10 p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{r.tenantName}</span>
                {r.tenantSlug && <span className="opacity-60">{r.tenantSlug}</span>}
                <span className="opacity-60">· {r.userName}</span>
                <span className="opacity-60">· {fmt(r.createdAt)}</span>
                {r.context && <span className="opacity-60">· {r.context}</span>}
                {nickOf(r.context) && (
                  <a
                    className="rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-900 underline-offset-2 hover:underline"
                    href={`https://t.me/${nickOf(r.context)!.slice(1)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    написать {nickOf(r.context)}
                  </a>
                )}
                <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${r.status === "open" ? "bg-orange-100 text-orange-800" : "bg-black/5"}`}>
                  {r.status === "open" ? "открыто" : "закрыто"}
                </span>
              </div>
              <div className="mt-2 whitespace-pre-wrap text-sm">{r.text}</div>
              {r.status === "done" ? (
                <div className="mt-2 text-sm opacity-70">
                  Ответ: {r.note ?? "—"}
                  <button className="ml-3 underline" disabled={busy === r.id} onClick={() => void resolve(r.id, true)}>
                    Открыть снова
                  </button>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    className="min-w-[280px] flex-1 rounded-xl border border-black/10 px-3 py-2 text-sm"
                    placeholder="Ответ автору (придёт уведомлением)"
                    value={note[r.id] ?? ""}
                    onChange={(e) => setNote({ ...note, [r.id]: e.target.value })}
                  />
                  <button className="rounded-xl bg-black px-4 py-2 text-sm text-white" disabled={busy === r.id} onClick={() => void resolve(r.id)}>
                    Ответить и закрыть
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Канал в Telegram (07.10): подключён ли бот поддержки и чем кончилась
// последняя отправка. Обращение сохраняется здесь в любом случае — но если
// канал молчит, оператор узнаёт об обращении только открыв консоль.
type Channel = {
  tokenSet: boolean;
  chatSet: boolean;
  chat: string | null;
  migratedFrom: string | null;
  last: { at: string; ok: boolean; error: string | null } | null;
  ok?: boolean;
};

function HelpChannel({ apiKey }: { apiKey: string }) {
  const [ch, setCh] = useState<Channel | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    void opApi<Channel>(apiKey, "opHelpChannel").then((r) => (r.ok ? setCh(r.data) : setErr(r.error)));
  }, [apiKey]);

  async function test() {
    setBusy(true);
    const r = await opApi<Channel>(apiKey, "opHelpChannelTest");
    setBusy(false);
    if (!r.ok) return setErr(r.error);
    setErr("");
    setCh(r.data);
  }

  if (!ch) return err ? <div className="text-sm text-red-600">{err}</div> : null;
  const broken = !ch.tokenSet || !ch.chat || (ch.last && !ch.last.ok);
  const when = ch.last ? new Date(ch.last.at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <div className={`rounded-2xl border p-3 text-sm ${broken ? "border-red-300 bg-red-50" : "border-black/10"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-medium">Telegram поддержки:</span>
        <span>
          {!ch.tokenSet
            ? "бот не подключён — нет TELEGRAM_HELP_BOT_TOKEN"
            : !ch.chat
              ? "чат не задан — нет TELEGRAM_HELP_CHAT_ID, напишите боту в группе"
              : ch.last
                ? ch.last.ok
                  ? `работает · последнее сообщение ушло ${when}`
                  : `не доходит · ${when}: ${ch.last.error}`
                : `подключён, чат ${ch.chat} · с последнего выката ещё ничего не отправляли`}
        </span>
        <button className="ml-auto rounded-full border border-black/15 px-3 py-1" disabled={busy || !ch.tokenSet} onClick={() => void test()}>
          {busy ? "Отправляю…" : "Отправить проверку"}
        </button>
      </div>
      {ch.migratedFrom && (
        <div className="mt-1.5 opacity-80">
          Группа стала супергруппой: пишем в новый чат {ch.chat} вместо {ch.migratedFrom}. Попросите Игоря положить {ch.chat} в
          TELEGRAM_HELP_CHAT_ID.
        </div>
      )}
      {err && <div className="mt-1.5 text-red-600">{err}</div>}
    </div>
  );
}
